/**
 * Eastview color system — the single source of color for the product.
 * The reasoning behind each choice lives in ./eastview-color-system.md; this
 * file is the implementation. Do NOT use MUI's grey[100]…grey[900] or invent a
 * new teal/green/amber/red per screen — import from here.
 *
 * The one rule that explains most of the file: **the brand teal is a surface
 * color, not a text color.** White on the brand `#1BA3B8` measures 3.0:1 — fine
 * for a header or a 2px indicator, an AA failure under a 13px button label. So
 * the ramp splits the two jobs:
 *
 *   teal[500] — BRAND SURFACE: header, tab indicators, progress fills
 *   teal[600] — PRIMARY ACTION: filled buttons, links (4.5:1, the lightest
 *               passing step — still reads as the same bright teal)
 *   teal[700] — ACTIVE TEXT: selected tabs/rows/sidebar items, button hover
 *
 * Picking a step: tints 50–100, borders-on-tint 200, focus ring 300,
 * decorative 400, header surface 500, interactive 600, hover 700, pressed 800,
 * dark chrome 900.
 *
 * Cross-repo note: `tpo-portal-frontend` and `lendmarq-los` carry the same file
 * shape with their own brand ramp (MARQ #56A0D3 at 400, Lendmarq #1F5AF2 at
 * 600). Slate, ink and the status pairs are shared verbatim across all three —
 * only the brand hue differs, which is what makes cross-product work painless.
 */

// Teal ramp — hue held at ~189°, derived from the Eastview brand #1BA3B8.
// 100 / 500 / 700 were sampled from the live product (active-state tint, app
// header, active text); the rest of the ramp is built around them.
export const teal = {
  50: "#F2FAFB", // hover fills on white, faint washes
  100: "#E4F4F7", // sampled — active-state tint (selected tabs, rows, cards)
  150: "#C8EBEA", // hairline on a teal-50 wash (calculated / "auto" blocks)
  200: "#BFE4EB", // borders on tinted surfaces
  300: "#8FD0DC", // focus rings
  400: "#4FBACB", // decorative accents, hover for elements on the teal header
  500: "#1BA3B8", // sampled — BRAND SURFACE: header, indicators, progress fills
  600: "#0F8296", // primary action — buttons, links (4.5:1 on white)
  700: "#0C6B7C", // sampled — active/selected text, primary hover (6.2:1)
  800: "#09525F", // primary pressed (8.8:1)
  900: "#0A4450", // dark chrome (the pinned-loan strip and similar)
};

/**
 * The teal baked into the logo PNGs as their background matte.
 *
 * NOT `teal[500]` — the asset was exported at #1C97C1, a few points off the
 * sampled brand teal. Anything that sets a colored bar BEHIND the logo image
 * must use this, not the brand token: the logo carries its own opaque
 * background, so a bar in any other shade shows a visible seam around the image.
 * Track it to the asset (src/assets/logos/*-logo.png); if the logo is
 * re-exported, resample and update here.
 */
export const brandLogoMatte = "#1C97C1";

// Form field text (2026-10-08 standard — CLAUDE.md "Form field labels &
// helpers"): the label above a control is near-black ink (Tailwind gray-900)
// and the helper under it is slate-500. Recipes in formStyles.js
// (`FIELD_TEXT`); never type these hexes into a component.
export const formFieldText = {
  label: "#111827",
  helper: "#64748B",
};

// A quiet white secondary button in a page header (e.g. Request Board →
// Alerts): label and icon in Tailwind gray-700 on a white face.
export const neutralButton = {
  text: "#374151",
};

// Cool-gray neutrals (Tailwind slate), same temperature family as the teal.
// Shared verbatim with the sibling products.
export const slate = {
  50: "#F8FAFC",
  100: "#F1F5F9",
  200: "#E2E8F0",
  300: "#CBD5E1",
  400: "#94A3B8",
  500: "#64748B",
  600: "#475569",
  700: "#334155",
  800: "#1E293B",
  900: "#0F172A",
};

// Anchors the system: headings, primary body text (16.9:1).
export const ink = slate[900];

/**
 * Second accent hue, and the level-2 active color (see `level2` below). Steel
 * blue already carries stage bars and the second chart series, so "a slice of
 * the current page" and "secondary data" share one hue on purpose.
 *
 * Never for buttons — secondary buttons are neutral (white / slate-300 border /
 * slate-700 text).
 */
export const steel = { main: "#2C5282", tint: "#E8EEF6" };

/** Sampled — the neutral segmented-control track (level 3). */
export const segTrack = "#F5F7F8";

/**
 * Two-level active states — the thing that keeps nested switchers legible.
 *
 * The product nests switchers inside switchers (a page tab containing a tier
 * selector). Two adjacent active states in the same color make it ambiguous
 * which level is "where you are" and which is "what you're looking at", so
 * there are two active colors, one per level, plus a neutral third.
 *
 * **The test:** does the URL/breadcrumb change? → `level1`. Does the data
 * change but not the location? → `level2`. Only the presentation? → `level3`.
 */

/** LEVEL 1 — place ("where am I"): page tabs, sub-nav, sidebar, page rails. */
export const level1 = {
  tint: teal[100],
  text: teal[700],
  indicator: teal[500], // 2px — underline horizontal, left/right bar vertical
  border: teal[200], // on selected cards
  hoverTint: teal[50],
  restText: slate[500],
  weight: 600,
};

/** LEVEL 2 — selection ("what am I looking at"): in-page data switchers. */
export const level2 = {
  tint: steel.tint,
  text: steel.main,
  indicator: steel.main,
  border: "#C3D2E6",
  hoverTint: "#F3F6FB",
  restText: slate[500],
  weight: 600,
};

/**
 * LEVEL 3 — utility ("how am I viewing it"): view-mode / cosmetic toggles.
 * The white-card-in-gray-track segmented pattern. No hue: these choices carry
 * no informational state worth a color.
 */
export const level3 = {
  track: segTrack,
  activeBg: "#FFFFFF",
  activeText: ink,
  activeShadow: "0 1px 3px rgba(15,23,42,.12)",
  restText: slate[500],
  weight: 600,
};

/**
 * The teal app header — a *light colored* surface, not a dark one, and that is
 * the constraint that shapes it: nothing in the 500–600 range may sit on it
 * (teal on teal disappears, and a teal-600 fill has too little contrast against
 * its own background). White takes over every role — labels, the active pill,
 * outlines. White buttons on the header carry a `teal[700]` label.
 *
 * Pick the pill **or** an underline for the active nav item, product-wide — not
 * both. This build uses the pill.
 */
export const onHeader = {
  bg: teal[500],
  // The header keeps a subtle vertical gradient; the top stop is teal-500
  // lightened 6% so it stays on-ramp rather than being its own color.
  gradient: `linear-gradient(180deg, #29A9BC, ${teal[500]})`,
  fg: "#FFFFFF",
  // Inactive nav items / icon buttons. Don't go lighter — white on teal-500 is
  // already only 3.0:1, so header text also stays at 13px+ / weight 500.
  fgMuted: "rgba(255,255,255,.82)",
  hoverBg: "rgba(255,255,255,.10)",
  activeBg: "rgba(255,255,255,.22)", // the active nav pill
  // The dashboard header's tab strip is the one place that carries both
  // signals: its tabs sit flush on the header's bottom edge, so the white
  // underline is the primary marker and this wash just lifts the active tab off
  // the teal. Keep it well under `activeBg` — it is a hint, not a pill.
  tabActiveBg: "rgba(255,255,255,.16)",
  buttonBorder: "rgba(255,255,255,.6)",
  buttonFg: teal[700], // label color for a white pill button on the header
  focusRing: "rgba(255,255,255,.55)",
  // Dark chrome hanging off the header (the pinned-loan strip).
  darkBg: teal[900],
};

/**
 * Status tint pairs — light background + darker text of the same hue. Pin these
 * exact pairs so every pill in the product matches; `solid` is for filled
 * alerts and destructive buttons.
 *
 * `info` ("in progress") is **steel, not blue**: with a teal brand, a
 * blue-tinted pill would read as *selected* rather than as a status.
 */
export const status = {
  success: { bg: "#ECFDF5", fg: "#047857", solid: "#15803D" }, // completed / published
  info: { bg: steel.tint, fg: steel.main, solid: steel.main }, // in progress
  warning: { bg: "#FFFBEB", fg: "#B45309", solid: "#B45309" }, // revisions needed
  neutral: { bg: slate[100], fg: slate[500], solid: slate[500] }, // not started
  // declined / error. `title` / `body` are the text pair for a red-BACKGROUND
  // alert box (callout / banner on `bg`) — darker than `fg` so a headline and
  // its explanation both read against the tint. Pills and dots keep `fg`.
  error: {
    bg: "#FEF2F2",
    fg: "#B91C1C",
    solid: "#B91C1C",
    title: "#991B1B",
    body: "#7F1D1D",
  },
  // "A required entry is missing" — the closing calendar's Not scheduled
  // level. Capital Markets still counts it as a red signal, but at chip size
  // a second red is indistinguishable from Blocked, so it gets its own hue
  // (magenta): unset / needs data entry, not a hard stop.
  missing: { bg: "#FDF4FF", fg: "#A21CAF", solid: "#C026D3" },
};

/**
 * The KIND of a change on the Changelog ("What's new") page — the pill on each
 * row and the dot in the header breakdowns. Scoped to that page on purpose:
 * it is a familiar green / blue / red "release notes" vocabulary, and its blue
 * is deliberately NOT the system's `info` (which is steel). Don't reuse these
 * for statuses elsewhere — use `status`.
 */
export const changeKind = {
  new: { bg: "#DCFCE7", fg: "#15803D", dot: "#22C55E" },
  improved: { bg: "#DBEAFE", fg: "#1D4ED8", dot: "#3B82F6" },
  fixed: { bg: "#FEE2E2", fg: "#B91C1C", dot: "#EF4444" },
  // The change text itself; its bold feature label stays slate-800.
  text: "#52606D",
};

/**
 * Amber stays as a product accent (`color="amber"` on Button/Chip) — it is not
 * part of the color system's reasoning, just the one warm accent the app
 * already ships. Kept here so it stops being redefined per screen.
 */
/**
 * The changelog's "Admin" chip — a pale yellow (Tailwind yellow-100) with
 * yellow-800 text (~6.5:1), the same soft 100-tint / dark-text recipe as the
 * New / Improved / Fixed pills, so it sits with them instead of shouting.
 */
export const adminChip = { bg: "#FEF9C3", fg: "#854D0E" };

export const amber = {
  main: "#E08A1E",
  light: "#FAEEDA",
  dark: "#B86F11",
  chipText: "#854F0B",
};

/**
 * Corner radius for anything that sits in a form row and reads as a field.
 * Hand-rolled controls (a summary row beside a real input) can't inherit the
 * theme's MuiOutlinedInput override, so they import this instead of picking a
 * number. Change it here and in generateMuiThemeDefaults together.
 */
export const INPUT_BORDER_RADIUS = 8;

const colors = {
  // Flat slate aliases — the shape the app already imports.
  slate900: slate[900],
  slate800: slate[800],
  slate700: slate[700],
  slate600: slate[600],
  slate500: slate[500],
  slate400: slate[400],
  slate300: slate[300],
  slate200: slate[200],
  slate100: slate[100],
  slate50: slate[50],

  white: "#FFFFFF",
  black: "#000000",
  ink,

  // Teal ramp, flat.
  teal50: teal[50],
  teal100: teal[100],
  teal150: teal[150],
  teal200: teal[200],
  teal300: teal[300],
  teal400: teal[400],
  teal500: teal[500],
  teal600: teal[600],
  teal700: teal[700],
  teal800: teal[800],
  teal900: teal[900],

  // Role aliases — prefer these at call sites; they say what the color is FOR.
  // `brand` and `action` are deliberately DIFFERENT here: see the header note.
  brand: teal[500], // surface / indicator only — never under text
  logoMatte: brandLogoMatte, // the bar color BEHIND a logo image — see above
  accent: teal[400], // decorative color next to text
  action: teal[600], // color under white text
  actionHover: teal[700],
  actionPressed: teal[800],
  focusRing: teal[300],
  tint: teal[100], // active-state / selected fill
  tintFg: teal[700], // text on a teal tint
  tintBorder: teal[200],

  // Header surface (see `onHeader` for the full on-header set).
  headerBg: onHeader.bg,
  headerFg: onHeader.fg,
  headerFgMuted: onHeader.fgMuted,

  // Second accent / level-2 active.
  steel: steel.main,
  steelTint: steel.tint,

  segTrack,

  // Semantic solids + tint backgrounds. `*Fg` is the tint pair's text color.
  success: status.success.solid,
  successBg: status.success.bg,
  successFg: status.success.fg,
  warning: status.warning.solid,
  warningBg: status.warning.bg,
  warningFg: status.warning.fg,
  danger: status.error.solid,
  dangerBg: status.error.bg,
  dangerFg: status.error.fg,
  info: status.info.solid,
  infoBg: status.info.bg,
  infoFg: status.info.fg,
  neutralBg: status.neutral.bg,
  neutralFg: status.neutral.fg,
};

export default colors;
